import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import { Alert, Breadcrumb, Card } from "components/ui/BootstrapCompat";
import useLocalStorage from "helpers/useLocalStorage";
import { donateReferralGetPolicy } from "services/main/donate.service";

const fallbackReferralPolicyMarkdown = `# ເງື່ອນໄຂລາຍໄດ້ຈາກການແນະນຳໝູ່

> ຕອນນີ້ອັດຕາເປີເຊັນ referral ຍັງລໍຖ້າ policy ແລະ backend API ຢືນຢັນ. ໜ້າລາຍໄດ້ຈະສະແດງຕາມ reward/commission field ທີ່ backend ສົ່ງກັບມາເທົ່ານັ້ນ.

## ອັດຕາຜົນຕອບແທນ

**ລໍຖ້າກຳນົດ %**

ຫາກມີ campaign ທີ່ກຳນົດເປີເຊັນແລ້ວ backend ຄວນສົ່ງ reward amount, currency ແລະ payout state ກັບມາໃຫ້ກວດສອບໄດ້.

## ໄດ້ຈາກຫຍັງແດ່

- ໄດ້ຈາກການທີ່ໝູ່ສະໝັກບັນຊີຜ່ານ referral link ຫຼືໃສ່ referral code ຂອງທ່ານ.
- ລະບົບຕ້ອງຜູກບັນຊີໃໝ່ກັບ code ຂອງຜູ້ແນະນຳກ່ອນ ຈຶ່ງຈະນັບເປັນ referral.
- ການນັບລາຍໄດ້ຈະອີງຕາມສະຖານະ reward ທີ່ backend ກຳນົດ ເຊັ່ນ pending, valid, rewarded ຫຼື rejected.
- ຖ້າ code ຜິດ, ສະໝັກຊ້ຳ, ຫຼືເປັນການແນະນຳໂຕເອງ ອາດບໍ່ຖືກນັບລາງວັນ.

## ຂັ້ນຕອນການກົດແນະນຳ

1. Copy link ແນະນຳຈາກໜ້າການແນະນຳໝູ່.
2. ສົ່ງ link ໃຫ້ໝູ່ຜ່ານ WhatsApp, Facebook, Line ຫຼືຊ່ອງທາງອື່ນ.
3. ໃຫ້ໝູ່ກົດ link ແລະສະໝັກຜ່ານໜ້າ register.
4. ເມື່ອສະໝັກສຳເລັດ backend ຈະບັນທຶກ referrer/referral code.
5. ລາຍໄດ້ຫຼື commission ຈະສະແດງເມື່ອ backend ສົ່ງຂໍ້ມູນ reward/commission ກັບມາ.

## ການຈ່າຍລາງວັນ

- ລາງວັນຕ້ອງຜ່ານການກວດສອບສະຖານະຈາກ backend.
- ລາຍການທີ່ຖືກອະນຸມັດຈະເປັນສະຖານະ rewarded.
- ຖ້າມີການຖອນຫຼືຈ່າຍອອກ ຄວນມີ payout state ແລະ audit log.
`;

const getPolicyMarkdown = (response: any) =>
  String(
    response?.data?.markdown ||
      response?.data?.contentMarkdown ||
      response?.data?.policyMarkdown ||
      response?.markdown ||
      response?.contentMarkdown ||
      response?.policyMarkdown ||
      "",
  ).trim();

const renderInlineMarkdown = (text: string) => {
  const nodes: React.ReactNode[] = [];
  const pattern = /(\*\*([^*]+)\*\*|\[([^\]]+)\]\((https?:\/\/[^)]+)\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text))) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[2]) {
      nodes.push(
        <strong key={`${match.index}-strong`} className="font-black">
          {match[2]}
        </strong>,
      );
    } else if (match[3] && match[4]) {
      nodes.push(
        <a
          key={`${match.index}-link`}
          href={match[4]}
          target="_blank"
          rel="noreferrer"
          className="font-bold text-primary-500 underline"
        >
          {match[3]}
        </a>,
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

const MarkdownBlock = ({ markdown }: { markdown: string }) => {
  const lines = markdown.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let listItems: string[] = [];
  let orderedItems: string[] = [];

  const flushLists = () => {
    if (listItems.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="mb-3 list-disc space-y-1 pl-5">
          {listItems.map((item) => (
            <li key={item}>{renderInlineMarkdown(item)}</li>
          ))}
        </ul>,
      );
      listItems = [];
    }

    if (orderedItems.length) {
      blocks.push(
        <ol
          key={`ol-${blocks.length}`}
          className="mb-3 list-decimal space-y-1 pl-5"
        >
          {orderedItems.map((item) => (
            <li key={item}>{renderInlineMarkdown(item)}</li>
          ))}
        </ol>,
      );
      orderedItems = [];
    }
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();

    if (!line) {
      flushLists();
      return;
    }

    const unorderedMatch = line.match(/^[-*]\s+(.+)$/);
    const orderedMatch = line.match(/^\d+\.\s+(.+)$/);

    if (unorderedMatch) {
      orderedItems = [];
      listItems.push(unorderedMatch[1]);
      return;
    }

    if (orderedMatch) {
      listItems = [];
      orderedItems.push(orderedMatch[1]);
      return;
    }

    flushLists();

    if (line.startsWith("# ")) {
      blocks.push(
        <h1
          key={index}
          className="mb-2 text-[22px] font-black leading-tight text-[#101828] dark:text-slate-100"
        >
          {renderInlineMarkdown(line.replace(/^#\s+/, ""))}
        </h1>,
      );
      return;
    }

    if (line.startsWith("## ")) {
      blocks.push(
        <h2
          key={index}
          className="mb-2 mt-4 text-[16px] font-black text-[#101828] dark:text-slate-100"
        >
          {renderInlineMarkdown(line.replace(/^##\s+/, ""))}
        </h2>,
      );
      return;
    }

    if (line.startsWith("> ")) {
      blocks.push(
        <Alert key={index} variant="warning" className="mb-3">
          {renderInlineMarkdown(line.replace(/^>\s+/, ""))}
        </Alert>,
      );
      return;
    }

    blocks.push(
      <p key={index} className="mb-3">
        {renderInlineMarkdown(line)}
      </p>,
    );
  });

  flushLists();

  return (
    <div className="text-[13px] leading-6 text-[#475467] dark:text-slate-300">
      {blocks}
    </div>
  );
};

export default function ReferralDetailPage() {
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");
  const { data, isLoading, isError } = useQuery(
    ["referral-policy-markdown", token, businessId],
    () => donateReferralGetPolicy({ token, businessId }),
    {
      enabled: Boolean(token),
      retry: false,
    },
  );
  const backendMarkdown = getPolicyMarkdown(data);
  const markdown = backendMarkdown || fallbackReferralPolicyMarkdown;

  return (
    <div className="min-w-0">
      <Breadcrumb>
        <Breadcrumb.Item active>ການຕັ້ງຄ່າ</Breadcrumb.Item>
        <Breadcrumb.Item active>ການແນະນຳໝູ່</Breadcrumb.Item>
        <Breadcrumb.Item active>ລາຍລະອຽດ</Breadcrumb.Item>
      </Breadcrumb>

      <div className="mb-2 flex flex-col gap-2 rounded bg-white p-2 dark:bg-slate-950 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="mb-1 text-[22px] font-black leading-tight text-[#101828] dark:text-slate-100">
            ເງື່ອນໄຂລາຍໄດ້ຈາກການແນະນຳໝູ່
          </h1>
          <div className="text-[12px] text-gray-600 dark:text-slate-300">
            ຂໍ້ມູນນີ້ຮອງຮັບ markdown ຈາກ backend
          </div>
        </div>
        <Link to="/referrals" className="btn btn-outline-primary">
          ກັບໄປໜ້າການແນະນຳ
        </Link>
      </div>

      {isLoading && (
        <Alert variant="info" className="mb-2">
          ກຳລັງໂຫລດເງື່ອນໄຂຈາກ backend...
        </Alert>
      )}

      {isError && (
        <Alert variant="warning" className="mb-2">
          ຍັງບໍ່ສາມາດໂຫລດ markdown ຈາກ backend ໄດ້. ຕອນນີ້ສະແດງເນື້ອຫາ
          fallback ກ່ອນ.
        </Alert>
      )}

      <Card>
        <Card.Header>Referral policy markdown</Card.Header>
        <Card.Body>
          <MarkdownBlock markdown={markdown} />
        </Card.Body>
      </Card>
    </div>
  );
}
